// The `table_*` events, shared by the two table components that exist in this app so their names
// and props cannot drift apart:
//
//   - `@components/AppTableSeeker` — the wrapper around the package's `TableSeeker`, which every
//     page uses (71 call sites).
//   - `@components/AppTableCore`   — wraps mui-datatables directly and survives on one page,
//     Monitoring › Results.
//
// Both hand mui-datatables an `onTableChange`, which is the single hook every table interaction
// passes through, so one function covers search, sort, pagination, columns and filters for the
// whole app without a page carrying analytics code.
//
// Names and props are PUIG's (`puig-app-front-v2/src/@components/AppTableCore`), so a query written
// for one app answers the same question on the other — the point of the rollout standard (PRD
// *Product Analytics for All Company Apps*). One deliberate divergence: `table_searched` sends the
// **length** of what was typed, never the text. PRD §7 forbids capturing input values, and PUIG's
// `query` prop contradicts it. See `dipi-app-kit/Docs/product-analytics-dipi.md`.

import { track } from "./index"

const TABLE_ACTIONS = {
    search: s => ['table_searched', { chars: (s.searchText || '').length }],
    sort: s => ['table_sorted', { column: s.sortOrder?.name, direction: s.sortOrder?.direction }],
    changePage: s => ['table_paginated', { page: s.page }],
    changeRowsPerPage: s => ['table_rows_per_page', { rows: s.rowsPerPage }],
    viewColumnsChange: s => ['table_columns_changed', { columns: s.columns?.filter(c => c.display === 'true').length }],
    filterChange: s => ['table_filtered', {}],
}

// Which table it was. `screen` is already on the envelope, so this only earns its place on screens
// showing more than one table — Stock Details, for instance. PUIG reads `title` and gives up when it
// is not a string; here almost every title is a component (`<TitleWithPopover title="…"/>`), so its
// own `title` prop is unwrapped before giving up.
function tableLabel(title) {
    if (typeof title === 'string') return title
    const inner = title?.props?.title
    return typeof inner === 'string' ? inner : undefined
}

// Call from a table's `onTableChange`, then delegate to whatever the caller passed.
export function reportTableChange(action, state, title) {
    try {
        const map = TABLE_ACTIONS[action]
        if (!map) return
        const [event, props] = map(state)
        track(event, { ...props, table: tableLabel(title) })
    } catch { /* fail-silent — analytics never breaks a table */ }
}

// The Excel download does not go through `onTableChange`: mui-datatables has its own `onDownload`,
// and both components wrap the caller's. Kept here so the event name lives in one place.
export function reportExcelDownload(option) {
    try {
        track('excel_downloaded', option ? { option } : undefined)
    } catch { /* fail-silent */ }
}
